import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

import { subscribeToJam, type JamPlaybackEvent } from '../../lib/realtime';
import { useNotice } from '../../stores/notice';
import { usePlayer, type PlayableTrack } from '../../stores/player';
import { useSession } from '../../stores/session';
import { MY_JAM_KEY, jams, type Jam, type JamPlayback, type JamSeed } from './api';

/**
 * The jam this person is in, kept fresh, and the player kept with it.
 *
 * ## One subscription, wherever it is asked from
 *
 * Several surfaces want to know about the jam - the page, the plus menu,
 * the player bar - and each asking the socket for itself would be several
 * channel subscriptions for one fact. So the query is the fact and
 * `JamWatcher`, mounted once in the shell, is the only thing that listens:
 * on `jam.changed` it refetches, and every surface reading the query moves.
 *
 * ## Everybody hears the same second
 *
 * The jam has a clock: which queue item, whether it is playing, and where
 * it was at a known server time. While this person's player is playing
 * *from* the jam (`source === 'jam'`):
 *
 *   - every local action - play, pause, skip, a seek - is sent to the jam,
 *     so the others follow it;
 *   - every `jam.playback` from somebody else is applied here - the same
 *     track, the same play state, and the playhead moved if it is more than
 *     `DRIFT` seconds from where the clock says - and between readings a
 *     guest keeps checking itself against the last one;
 *   - the host's player reports its position every `HEARTBEAT` seconds, so
 *     a player that has drifted, or joined late, is pulled back onto it.
 *
 * The clock is the server's. `at` and `server_time` come from the same
 * response, so the difference between `server_time` and `Date.now()` is
 * this device's offset, and the expected position is arithmetic on that -
 * nobody's wall clock is trusted, including the host's.
 *
 * ## Echoes, and telling local from remote
 *
 * Applying a remote state changes the player, and a store subscription
 * that reports every change would send that right back. So an applied
 * state is remembered, and a change that matches it is not reported. The
 * server also names who set each state, and a player ignores its own.
 */
const DRIFT = 0.8;
/** How long a guest gives the host to report an advance before saying it itself. */
const ADVANCE_WAIT = 1_500;
/** And how long a clock naming the track it just left is treated as stale. */
const ADVANCE_GRACE = 6_000;
const HEARTBEAT = 8_000;

/**
 * The player, as a seed: its queue in the order it walks it (shuffle and
 * all), where it is, and what it is playing through. Null when nothing is
 * playing, which starts an empty jam as before.
 */
export function seedFromPlayer(): JamSeed | null {
  const state = usePlayer.getState();

  if (state.track === null || state.queue.length === 0) return null;

  // The walk, not the list: what the host hears next is what the jam plays next.
  const order = state.order.length === state.queue.length ? state.order : state.queue.map((_, i) => i);
  const ids = order.map((i) => state.queue[i]?.id).filter((id): id is number => typeof id === 'number');
  const index = Math.max(0, ids.indexOf(state.track.id));

  return {
    kind: state.source === 'collection' && state.origin ? 'collection' : 'tracks',
    id: state.origin?.id ?? null,
    label: state.origin?.title ?? labelFor(state.source),
    track_ids: ids,
    index,
    position_ms: Math.round(state.position * 1000),
    playing: state.playing,
  };
}

function labelFor(source: string): string | null {
  switch (source) {
    case 'feed':
      return 'your feed';
    case 'profile':
      return 'a profile';
    case 'chart':
      return 'the charts';
    case 'search':
      return 'search';
    case 'dashboard':
      return 'new music';
    default:
      return null;
  }
}

/**
 * Start a jam from wherever "Start a jam" is pressed.
 *
 * Seeded from what is playing, so the music carries on for the host
 * without a gap: the jam's clock is set to where the host is, and the
 * player is switched to the jam's copy of its own queue in place, no
 * reload. With nothing playing, an empty jam, and the first thing played
 * becomes the context (see `JamWatcher`).
 */
export async function startJam(client: QueryClient): Promise<Jam> {
  const seed = seedFromPlayer();
  const jam = await jams.create(seed ?? undefined);

  client.setQueryData(MY_JAM_KEY, { jam });
  client.setQueryData(['jam', jam.code], jam);

  if (seed !== null) {
    adoptQueue(jam);
  }

  return jam;
}

/**
 * The player takes the jam's queue as its own, keeping the track it is on
 * and where it is in it. Not a local action: the clock was just set from
 * this player, so nothing is reported back.
 */
/**
 * Where this player sits in the jam's queue: the clock's item first, and
 * only then the first track that matches.
 *
 * By track alone was wrong whenever a track was in the queue twice - added
 * by somebody and also part of the album playing through - because the
 * first match could be one the room had already heard, and the player then
 * walked forward from there, replaying it and reporting each step.
 */
function placeIn(jam: Jam, trackId: number | null): number {
  const byItem = jam.queue.findIndex((item) => item.item_id === jam.playback.item_id);

  if (byItem !== -1 && (trackId === null || jam.queue[byItem]?.track.id === trackId)) return byItem;

  return trackId === null ? -1 : jam.queue.findIndex((item) => item.track.id === trackId);
}

function adoptQueue(jam: Jam): void {
  const state = usePlayer.getState();
  const tracks = jam.queue.map((i) => i.track);
  const index = placeIn(jam, state.track?.id ?? null);

  clock.suppress = true;
  state._sync({
    queue: index === -1 && state.track ? [state.track, ...tracks] : tracks,
    index: index === -1 ? 0 : index,
    order: (index === -1 && state.track ? [state.track, ...tracks] : tracks).map((_, i) => i),
    source: 'jam',
  });
  remember(usePlayer.getState());
  clock.suppress = false;
}

export function useMyJam() {
  const user = useSession((state) => state.user);

  return useQuery({
    queryKey: MY_JAM_KEY,
    queryFn: () => jams.mine(),
    enabled: Boolean(user),
    staleTime: 30_000,
  });
}

/**
 * The position the clock says the jam is at, in seconds.
 *
 * `receivedAt` is this device's time when the reading arrived, which paired
 * with `server_time` gives the offset between the two clocks; from then on
 * elapsed time on this device is elapsed time on the server.
 */
export function expectedPosition(playback: JamPlayback, receivedAt: number, now = Date.now()): number {
  if (playback.at === null) return playback.position_ms / 1000;

  const offset = playback.server_time - receivedAt;
  const elapsed = playback.playing ? Math.max(0, now + offset - playback.at) : 0;

  return (playback.position_ms + elapsed) / 1000;
}

/**
 * What the watcher knows, shared with `listen()` because joining the clock
 * is the one local action that must not be reported as setting it.
 *
 * `last` is the most recent reading from somebody else, and the reference
 * a guest's player corrects towards between heartbeats. It is cleared when
 * this player does something itself: after a local scrub, this player is
 * the reference until the next reading says otherwise.
 */
const clock = {
  suppress: false,
  known: { trackId: null as number | null, index: -1, playing: false, position: 0 },
  last: null as { playback: JamPlayback; itemId: number | null; receivedAt: number } | null,
  /**
   * A track this player moved on to by itself that the room has not been
   * told about yet. The host is given the first word, and until it takes
   * it, a clock still naming the track we just left is stale and must not
   * drag us back.
   */
  advance: null as { trackId: number; at: number } | null,
};

/**
 * The guest rules, off. Only when they were on: a store write is an event
 * to every subscriber, and the host-follow effect reads the first one
 * after a reload as the play it is waiting for.
 */
function release(): void {
  const state = usePlayer.getState();

  if (state.intercept !== null || state.locked) state._sync({ intercept: null, locked: false });
}

function remember(
  state: { track: PlayableTrack | null; index?: number; playing: boolean; position: number },
): void {
  clock.known = {
    trackId: state.track?.id ?? null,
    // Where in the queue, not just which track: a room can move from one
    // copy of a track to another. See `stepped` in the watcher.
    index: state.index ?? -1,
    playing: state.playing,
    position: state.position,
  };
}

export function JamWatcher() {
  const client = useQueryClient();
  const me = useSession((state) => state.user);
  const { data } = useMyJam();
  const jam = data?.jam ?? null;
  const jamId = jam?.id ?? null;
  const code = jam?.code ?? null;
  const isHost = jam?.is_host ?? false;

  /*
   * The pages read the document, and its `playback` is what tells them
   * which row is playing. A clock change arrives on the socket or as the
   * answer to this player's own report, and neither used to touch the
   * document - so the jam page kept pointing at the track before last
   * until something else happened to refetch it.
   */
  const settleClock = useCallback(
    (playback: JamPlayback) => {
      if (code !== null) {
        client.setQueryData<Jam>(['jam', code], (held) => (held === undefined ? held : { ...held, playback }));
      }

      client.setQueryData<{ jam: Jam | null }>(MY_JAM_KEY, (held) =>
        held?.jam == null ? held : { ...held, jam: { ...held.jam, playback } },
      );
    },
    [client, code],
  );

  const latest = useRef<Jam | null>(null);
  latest.current = jam;

  const measuredAt = useRef(0);
  const wasIn = useRef<number | null>(null);

  // ---- following the jam ---------------------------------------------------
  useEffect(() => {
    if (jamId === null) return;

    const apply = (event: JamPlaybackEvent, fresh = false) => {
      if (event.by === me?.id) return;

      const state = usePlayer.getState();

      if (state.source !== 'jam') return;

      /*
       * A clock still on the track this player has just finished, while
       * its own advance is waiting to be reported. It is behind us, not
       * ahead: applying it would pull the music backwards and then the
       * report would push it forwards again.
       */
      if (
        clock.advance !== null
        && Date.now() - clock.advance.at < ADVANCE_GRACE
        && clock.advance.trackId === state.track?.id
      ) {
        const naming = latest.current?.queue.find((i) => i.item_id === event.item_id)?.track.id ?? null;

        if (naming !== state.track?.id) return;

        clock.advance = null;
      }

      const receivedAt = Date.now();
      const playback: JamPlayback = {
        item_id: event.item_id,
        playing: event.playing,
        position_ms: event.position_ms,
        at: event.at,
        server_time: event.server_time,
      };
      const current = latest.current;
      const item = current?.queue.find((i) => i.item_id === event.item_id) ?? null;
      const track = item?.track ?? null;

      // A clock on an item this copy of the queue does not have yet (added
      // and played in the same breath): fetch the queue rather than move
      // the track that is playing to the clock's position.
      if (event.item_id !== null && track === null) {
        if (fresh || code === null) return;

        void jams.get(code).then((latestJam) => {
          latest.current = latestJam;
          client.setQueryData(['jam', code], latestJam);
          client.setQueryData(MY_JAM_KEY, { jam: latestJam });
          apply(event, true);
        });

        return;
      }

      clock.last = { playback, itemId: event.item_id, receivedAt };

      settleClock(playback);

      /*
       * The room moved to another copy of the track this player is already
       * on - an added one the album was going to reach anyway. Nothing to
       * load, and nothing to hear, but the playhead has to agree about which
       * item it is on or its next report names the wrong one and drags the
       * clock backwards. See `itemPlaying`.
       */
      if (item !== null && track !== null && track.id === state.track?.id) {
        const at = current?.queue.findIndex((i) => i.item_id === item.item_id) ?? -1;

        if (at !== -1 && at !== state.index) state._sync({ index: at });
      }

      const target = () => expectedPosition(playback, receivedAt);

      if (track && track.id !== state.track?.id) {
        // A different track: load it, then land on the clock once decodable.
        const tracks = current?.queue.map((i) => i.track) ?? [track];
        // The clock's own item, not the first copy of its track: a jam can
        // hold the same track twice. See `itemPlaying`.
        const at = current?.queue.findIndex((i) => i.item_id === event.item_id) ?? -1;

        clock.suppress = true;

        void state.play(track, tracks, 'jam', undefined, at === -1 ? undefined : at).then(() =>
          landWhenReady(target, event.playing, () => {
            remember(usePlayer.getState());
            clock.suppress = false;
          }),
        );

        return;
      }

      clock.suppress = true;

      if (state.playing !== event.playing) state._sync({ playing: event.playing });

      if (Math.abs(state.position - target()) > DRIFT) state._seek(target());

      remember(usePlayer.getState());
      clock.suppress = false;
    };

    return subscribeToJam(
      jamId,
      () => {
        void client.invalidateQueries({ queryKey: MY_JAM_KEY });

        if (code !== null) void client.invalidateQueries({ queryKey: ['jam', code] });
      },
      apply,
    );
  }, [jamId, code, client, me?.id, settleClock]);

  // ---- reporting what this player does, and staying on the clock ------------
  useEffect(() => {
    if (code === null) return;

    const unsubscribe = usePlayer.subscribe((state) => {
      if (state.source !== 'jam' || state.loading) return;

      if (clock.suppress) {
        remember(state);

        return;
      }

      /*
       * The host holds the transport: nothing this player does is news to
       * the jam (the server would refuse it anyway), and the correction
       * below is all that is left of this effect for a guest.
       */
      const silent = !isHost && latest.current?.guests_control === false;

      const trackId = state.track?.id ?? null;
      const trackChanged = trackId !== clock.known.trackId;
      /*
       * The same track, in a different place in the queue.
       *
       * "Play that one again" puts a track the room has already heard back
       * in the queue, so the step from the old copy to the new one changes
       * nothing about what is coming out of the speakers - and the watcher,
       * which only ever compared track ids, said nothing. The clock stayed
       * on the copy behind the room, the added copy stayed listed as still
       * to come, and it never cleared however many times it played.
       */
      const stepped = !trackChanged && clock.known.index !== -1 && state.index !== clock.known.index;
      const playChanged = state.playing !== clock.known.playing;
      // A jump the playhead did not make on its own is a seek.
      const seeked = !trackChanged && Math.abs(state.position - clock.known.position) > 3;

      /*
       * A new track is reported the moment it is heard, not the moment it
       * is decodable: `at` then marks real audio, and the others land on
       * the same sound rather than on the second before it.
       */
      if (trackChanged && state.playing && state.position < 0.05) return;

      /*
       * The element pausing itself at the end of a track, a beat before
       * `ended`. Not a press, and not news.
       */
      const endedItself = playChanged && !state.playing && state.duration > 0 && state.position >= state.duration - 1;

      if (endedItself) {
        remember(state);

        return;
      }

      /*
       * A guest's player rolling on to the next track by itself.
       *
       * The host's clock reports that same advance, and it is the better
       * reference - it is the one everybody else is corrected towards, so
       * a guest reporting first only makes the room chase whichever
       * player decoded fastest. The host therefore gets the first word.
       *
       * But "the host reports it" holds only while the host is listening.
       * Paused, on another device, or an app in somebody's pocket, and
       * nobody said anything at all: the guest heard the next track while
       * the room's clock sat on the last one, and whatever had been added
       * stayed in the queue for ever. So the advance is remembered, a
       * clock still naming the old track is ignored while it waits (see
       * `apply`), and if nobody has spoken a beat later, this player does.
       */
      if (trackChanged && !isHost) {
        remember(state);

        const mine = state.track?.id ?? null;

        if (mine === null) return;

        clock.advance = { trackId: mine, at: Date.now() };

        window.setTimeout(() => {
          const now = usePlayer.getState();
          const jam = latest.current;

          if (clock.advance?.trackId !== mine) return;

          clock.advance = null;

          if (now.source !== 'jam' || now.track?.id !== mine || jam === null) return;

          const clockTrack = jam.queue.find((i) => i.item_id === jam.playback.item_id)?.track.id ?? null;

          if (clockTrack === mine) return;

          report(code, jam, now, now.playing, now.position, settleClock);
        }, ADVANCE_WAIT);

        return;
      }

      if ((trackChanged || stepped || playChanged || seeked) && !silent) {
        remember(state);
        // This player is the reference now, until somebody else says otherwise.
        clock.last = null;
        report(code, latest.current, state, state.playing, state.position, settleClock);

        return;
      }

      /*
       * Nothing changed here; is this player still where the clock says?
       * The host is the clock and has nothing to correct towards; a guest
       * drifts - a stall, a slow segment, a tab throttled - and this pulls
       * it back without waiting for the next heartbeat.
       */
      const last = clock.last;

      if (!isHost && last !== null && state.playing && last.playback.playing) {
        const onIt = last.itemId === null || latest.current?.queue.find((i) => i.item_id === last.itemId)?.track.id === trackId;
        const expected = expectedPosition(last.playback, last.receivedAt);

        if (onIt && Math.abs(state.position - expected) > DRIFT) {
          clock.suppress = true;
          state._seek(expected);
          remember(usePlayer.getState());
          clock.suppress = false;

          return;
        }
      }

      remember(state);
    });

    return unsubscribe;
  }, [code, isHost, settleClock]);

  // ---- a reload: back onto the jam's clock without asking ----------------------
  const rejoined = useRef<number | null>(null);

  useEffect(() => {
    if (jam === null || jam.status !== 'open' || rejoined.current === jam.id) return;

    rejoined.current = jam.id;

    /*
     * The player comes back from storage outside the jam (`source: 'track'`,
     * as it always has). If what it restored is what the jam is on, the
     * person was listening along a moment ago and still is: put the player
     * back on the jam and let the clock correction land it. Otherwise leave
     * it; "Listen along" is one press away.
     */
    const state = usePlayer.getState();
    const now = jam.queue.find((i) => i.item_id === jam.playback.item_id)?.track.id ?? null;

    if (state.source !== 'jam' && state.track !== null && now !== null && state.track.id === now) {
      adoptQueue(jam);
    }
  }, [jam]);

  // ---- the host plays something else: the jam plays through it ---------------
  const lastSource = useRef<string>('jam');

  useEffect(() => {
    if (code === null || !isHost) return;

    return usePlayer.subscribe((state) => {
      const was = lastSource.current;
      lastSource.current = state.source;

      /*
       * A play from outside the jam (a playlist, a profile, a track) while
       * hosting. Spotify replaces the jam's context silently and so does
       * this: the host's new queue becomes what the jam plays through, what
       * people added stays next, and the host's player is put back on the
       * jam's copy without interrupting what it just started.
       */
      // Not gated on `loading`: `play()` sets the source and `loading` in the
      // same write, and by the time loading clears the change has passed.
      if (was !== 'jam' || state.source === 'jam' || state.track === null) return;

      const seed = seedFromPlayer();

      if (seed === null) return;

      void jams
        .setContext(code, seed)
        .then((jam) => {
          client.setQueryData(MY_JAM_KEY, { jam });
          client.setQueryData(['jam', code], jam);
          adoptQueue(jam);
        })
        .catch(() => {
          // Refused (not the host any more, or the jam ended): the player
          // simply stays on what it is playing, outside the jam.
        });
    });
  }, [code, isHost, client]);

  // ---- the host's heartbeat --------------------------------------------------
  useEffect(() => {
    if (code === null || !isHost) return;

    const timer = setInterval(() => {
      const state = usePlayer.getState();

      if (state.source !== 'jam' || !state.playing || state.loading) return;

      // Landing on a guest's change: the position is not the host's yet, and
      // reporting it would send everybody back to the top of the track.
      if (clock.suppress) return;

      if (Date.now() - measuredAt.current < HEARTBEAT - 500) return;

      measuredAt.current = Date.now();
      report(code, latest.current, state, state.playing, state.position, settleClock);
    }, HEARTBEAT);

    return () => clearInterval(timer);
  }, [code, isHost, settleClock]);

  // ---- a guest's player: taps add, and the transport may be the host's ---------
  /*
   * The room's repeat, copied to the player so `step` can read it without
   * knowing what a jam is. The host owns it; everybody obeys it.
   */
  const repeats = jam?.repeats ?? false;

  useEffect(() => {
    const state = usePlayer.getState();

    if (state.jamRepeats !== repeats) state._sync({ jamRepeats: repeats });
  }, [repeats]);

  const guestsControl = jam?.guests_control ?? true;
  const guest = jam !== null && jam.status === 'open' && jam.is_member && !isHost;

  useEffect(() => {
    if (!guest || code === null) {
      release();

      return;
    }

    const jamCode = code;

    /*
     * A guest who taps a track is adding it to the jam, not leaving the jam
     * to play it. Spotify's guests work this way and it is the only reading
     * under which "I am in a jam" and "I pressed play on a song" both stay
     * true. Leaving is on the jam page, one tap.
     */
    usePlayer.getState()._sync({
      intercept: (track) => {
        void jams
          .add(jamCode, track.id)
          .then((fresh) => {
            client.setQueryData(MY_JAM_KEY, { jam: fresh });
            client.setQueryData(['jam', jamCode], fresh);
            useNotice.getState().show(`Added "${track.title}" to the jam`, `/jam/${jamCode}`);
          })
          .catch(() => useNotice.getState().show('Could not add that to the jam'));

        return true;
      },
      locked: !guestsControl,
    });

    return release;
  }, [guest, code, guestsControl, client]);

  // ---- the jam ending, or being left elsewhere --------------------------------
  useEffect(() => {
    if (jamId !== null) {
      wasIn.current = jamId;

      return;
    }

    if (wasIn.current === null) return;

    wasIn.current = null;
    clock.last = null;

    // What was playing carries on; nothing from the jam comes after it.
    const state = usePlayer.getState();

    if (state.source === 'jam') {
      state._sync({ queue: state.track ? [state.track] : [], index: 0, source: 'track' });
    }
  }, [jamId]);

  // ---- the player's copy of the queue -----------------------------------------
  useEffect(() => {
    if (jam === null) return;

    const state = usePlayer.getState();

    if (state.source !== 'jam') return;

    const fresh = jam.queue.map((item) => item.track);

    /*
     * Where the playhead lands in the rebuilt list: where it already is,
     * when the track there has not changed.
     *
     * This effect keeps the *list* in step with the room; moving the
     * playhead is the clock's job. Asking `placeIn` unconditionally meant
     * asking "where is this track", and a jam can hold the same track twice
     * - so a player that had just stepped onto the second copy was quietly
     * dragged back to the first one, reported from there, and everything in
     * between went back to being next in the jam. See `itemPlaying`.
     */
    const offset = Math.max(0, state.queue.length - jam.queue.length);
    const here = state.index - offset;
    const settled = state.track !== null && fresh[here]?.id === state.track.id;
    const index = settled ? here : placeIn(jam, state.track?.id ?? null);
    const queue = index === -1 && state.track ? [state.track, ...fresh] : fresh;

    state._sync({
      queue,
      index: index === -1 ? 0 : index,
      // The walk is the list: see `step`. Kept with the queue so a reorder
      // cannot leave an order pointing at positions that have moved.
      order: queue.map((_, i) => i),
    });
  }, [jam]);

  return null;
}

/**
 * Which queue item a player is on.
 *
 * By position first, and by track only as a fallback. A jam can hold the same
 * track twice - somebody adds a song the album was going to reach anyway, or
 * two people add the same one - and a search by track id names the first copy
 * every time. The clock then points at a place the room has already been, so
 * everything between the two copies goes back to being "next in the jam" and
 * the list never clears. That is the bug this exists for.
 *
 * The player's queue is the jam's, in the same order, unless it is playing
 * something the jam does not have: `placeIn` puts that one in front, and the
 * two lists differ by exactly that.
 */
function itemPlaying(
  jam: Jam,
  player: { track: PlayableTrack | null; queue: PlayableTrack[]; index: number },
) {
  const track = player.track;

  if (track === null) return null;

  const offset = Math.max(0, player.queue.length - jam.queue.length);
  const aligned = jam.queue[player.index - offset];

  if (aligned !== undefined && aligned.track.id === track.id) return aligned;

  return jam.queue.find((i) => i.track.id === track.id) ?? null;
}

/** Tell the jam what this player is doing. Fire and forget; the next one supersedes it. */
function report(
  code: string,
  jam: Jam | null,
  player: { track: PlayableTrack | null; queue: PlayableTrack[]; index: number },
  playing: boolean,
  position: number,
  settle?: (playback: JamPlayback) => void,
): void {
  const track = player.track;
  const item = jam === null ? null : itemPlaying(jam, player);

  /*
   * A track this copy of the queue cannot place says nothing at all.
   *
   * `item_id: null` is how a player says "nothing is playing", and it
   * emptied the clock for the whole room - every guest offered "Start the
   * music" on a jam that had been going for half an hour. A copy a second
   * out of date is the ordinary case, not an exceptional one, so this
   * waits for the next document rather than guessing. The server refuses
   * to be emptied this way either; both, because old builds exist.
   */
  if (track !== null && item === null) return;

  void jams.playback(code, {
    item_id: item?.item_id ?? null,
    playing,
    position_ms: Math.round(position * 1000),
  })
    .then(({ playback }) => settle?.(playback))
    .catch(() => {
      // A report that did not land is superseded by the next; nothing to do.
    });
}

/**
 * After a track load, move the playhead to the clock and set the play state,
 * once the stream is decodable - a seek before that is lost.
 */
function landWhenReady(target: () => number, playing: boolean, then?: () => void): void {
  let finished = false;

  const finish = () => {
    if (finished) return;

    finished = true;
    unsubscribe();
    then?.();
  };

  let landed = false;

  const unsubscribe = usePlayer.subscribe((state) => {
    if (state.loading || finished || landed) return;

    landed = true;

    // Evaluated now, so the time the stream took to load is in it.
    const at = target();

    if (Math.abs(state.position - at) > 0.5) state._seek(at);
    if (state.playing !== playing) state._sync({ playing });

    /*
     * Once more, two seconds on. A seek into a stream that is still
     * buffering lands late by however long the segment takes, and that is
     * the one lag the clock cannot see. The second pass measures it away.
     */
    setTimeout(() => {
      if (finished) return;

      const again = usePlayer.getState();
      const now = target();

      if (playing && again.playing && Math.abs(again.position - now) > 0.4) again._seek(now);

      // A beat later, so the seek has reached the store before the watcher
      // starts treating changes as news again.
      setTimeout(finish, 300);
    }, 2_000);
  });

  // A load that never finishes must not leave a subscription behind.
  setTimeout(finish, 20_000);
}

/** Everything that can be done to one jam, by its code. */
export function useJam(code: string) {
  const client = useQueryClient();
  const play = usePlayer((state) => state.play);

  const query = useQuery({
    queryKey: ['jam', code],
    queryFn: () => jams.get(code),
    /*
     * A backstop, not the mechanism. The socket carries the queue and the
     * clock within a beat; this is for the deployment with no realtime
     * configured and for the socket that quietly died, where a page that
     * says the room is on a track it finished five minutes ago is worse
     * than a request every few seconds.
     */
    refetchInterval: (q) => (q.state.data?.status === 'ended' ? false : 8_000),
    refetchOnWindowFocus: true,
  });

  const refresh = (fresh?: Jam) => {
    if (fresh) client.setQueryData(['jam', code], fresh);
    else void client.invalidateQueries({ queryKey: ['jam', code] });

    void client.invalidateQueries({ queryKey: MY_JAM_KEY });
  };

  return {
    ...query,
    join: useMutation({ mutationFn: () => jams.join(code), onSuccess: refresh }),
    leave: useMutation({
      mutationFn: () => jams.leave(code),
      onSuccess: () => {
        refresh();

        // Leaving takes the queue with it: what plays on is what was playing,
        // and nothing from a jam this person is no longer in comes after it.
        const state = usePlayer.getState();

        if (state.source === 'jam') {
          state._sync({ queue: state.track ? [state.track] : [], index: 0, source: 'track' });
        }
      },
    }),
    add: useMutation({
      mutationFn: (trackId: number) => jams.add(code, trackId),
      onSuccess: (fresh) => refresh(fresh),
    }),
    move: useMutation({
      mutationFn: ({ itemId, before }: { itemId: number; before: number | null }) =>
        jams.move(code, itemId, before),
      onSuccess: (fresh) => refresh(fresh),
      // A drag against a queue that has moved on: the refetch puts the list
      // back where the room actually is, which is the honest answer.
      onError: () => refresh(),
    }),
    settings: useMutation({
      mutationFn: (patch: { guests_control?: boolean; repeats?: boolean }) => jams.settings(code, patch),
      onSuccess: (fresh) => refresh(fresh),
    }),
    remove: useMutation({
      mutationFn: (itemId: number) => jams.remove(code, itemId),
      onSuccess: (fresh) => refresh(fresh),
    }),

    /**
     * Listen along: start this player where the jam is.
     *
     * If the jam is already playing something, that track at the clock's
     * position; if it has not started, the first item, which starts the
     * clock for everybody. Either way the player's source becomes the jam
     * and `JamWatcher` takes it from there.
     */
    listen: async () => {
      /*
       * A fresh reading, not the page's copy. The page fetched its document
       * when it opened, and the music may have started since; joining on a
       * stale "nothing playing" would start it again from the top for
       * everybody - which is what a listener least wants to do.
       */
      const receivedAt = Date.now();
      const jam = await jams.get(code);

      client.setQueryData(['jam', code], jam);

      const tracks = jam.queue.map((item) => item.track);
      const playing = jam.queue.find((item) => item.item_id === jam.playback.item_id) ?? jam.queue[0];

      if (!playing) return;

      const onClock = jam.playback.item_id === playing.item_id;

      const at = jam.queue.findIndex((item) => item.item_id === playing.item_id);

      if (!onClock) {
        // Nothing is playing yet: this press starts the music for everybody,
        // and is reported like any other local action.
        void play(playing.track, tracks, 'jam', undefined, at === -1 ? undefined : at);

        return;
      }

      /*
       * Joining a clock that is already running. Not a local action - the
       * others must not be dragged to wherever this player happens to load
       * - so the watcher is told to keep quiet until this player has landed.
       */
      clock.last = { playback: jam.playback, itemId: jam.playback.item_id, receivedAt };
      clock.suppress = true;

      void play(playing.track, tracks, 'jam', undefined, at === -1 ? undefined : at).then(() =>
        landWhenReady(
          () => expectedPosition(jam.playback, receivedAt),
          // A paused jam is joined paused.
          jam.playback.playing,
          () => {
            remember(usePlayer.getState());
            clock.suppress = false;
          },
        ),
      );
    },

    /** Play a particular item, for everybody. */
    playFrom: (jam: Jam, itemId: number) => {
      const tracks = jam.queue.map((item) => item.track);
      const at = jam.queue.findIndex((item) => item.item_id === itemId);
      const start = jam.queue.find((item) => item.item_id === itemId)?.track ?? tracks[0];

      // The item that was pressed, which is not the same thing as the first
      // row holding that track. See `itemPlaying`.
      if (start) void play(start, tracks, 'jam', undefined, at === -1 ? undefined : at);
    },
  };
}
